import type {UserId} from './id';

/**
 * Deleting a user empties their row of everything personal and leaves the row itself.
 *
 * The audit trail, the requests they submitted and the four-eyes record all point at that
 * id, and `request_created_by_fk` is `ON DELETE RESTRICT` — so removing the row is not a
 * choice between two designs, it is a statement the database refuses. ADR 0018.
 */

/** RFC 2606 reserves `.invalid`, so an anonymised address can never reach a mailbox. */
export const ANONYMOUS_EMAIL_DOMAIN = 'deleted.invalid';

export interface AnonymousIdentity {
  email: string;
  name: string;
}

/**
 * What replaces a deleted user's email and name. Derived from the id rather than random,
 * so a reference still says *which* user it was to anyone entitled to follow it, and the
 * unique index on the email column is satisfied without a lookup.
 *
 * The name is an identifier on purpose. "Deleted user" is a sentence, and a sentence
 * belongs in the locale files rather than in a column — ADR 0010.
 */
export const anonymousIdentityOf = (id: UserId): AnonymousIdentity => ({
  email: `deleted-${id}@${ANONYMOUS_EMAIL_DOMAIN}`,
  name: `deleted-${id}`,
});

/**
 * Whether an address belongs to the anonymous namespace. Live users are refused it, and a
 * CHECK constraint says the same thing: an address in this namespace exists exactly when
 * the row is deleted.
 *
 * Without that, an organization admin who can read their colleagues' ids could take
 * `deleted-<colleague>@deleted.invalid` for a user of their own, and deleting that
 * colleague would then fail on the unique index instead.
 */
export const isAnonymisedEmail = (email: string): boolean => email.toLowerCase().endsWith(`@${ANONYMOUS_EMAIL_DOMAIN}`);

/**
 * Whether an organization's scheduled deletion has come due. Both sides are `YYYY-MM-DD`,
 * where lexicographic order *is* chronological order, so no parsing is needed and no
 * timezone can creep in between them.
 *
 * The date is a standing rule rather than a one-shot: it stays set once it has passed, so
 * a user added to the organization afterwards is anonymised by the next run instead of
 * quietly surviving a wind-down that was already decided.
 */
export const isDeletionDue = (scheduledOn: string | null, today: string): boolean =>
  scheduledOn !== null && scheduledOn <= today;
